import { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Alert } from 'react-bootstrap';
// New package button
import { Button } from '@veeyaainnovatives/button';
import { useNavigate, useParams } from 'react-router-dom';
import logo from '../assets/images/sds-logo.svg';
import customerPortalImage from '../assets/images/customer-portal.png';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const CustomerResetPassword = () => {
    const { token, email } = useParams();
    const navigate = useNavigate();
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isVerifying, setIsVerifying] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [decodedEmail, setDecodedEmail] = useState('');

    // Decode email from URL (in case it's encoded)
    useEffect(() => {
        if (email) {
            try {
                setDecodedEmail(decodeURIComponent(email));
            } catch (e) {
                setDecodedEmail(email);
            }
        }
    }, [email]);

    // Verify token on component mount (optional step)
    useEffect(() => {
        const verifyToken = async () => {
            if (!token || !email) {
                setError('Invalid reset link. Please request a new password reset.');
                setIsVerifying(false);
                return;
            }

            try {
                setIsVerifying(true);
                const response = await axios.post(
                    getApiUrl('api/public/customer/verify-reset-token'),
                    {
                        token: token,
                        email: decodedEmail || email
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json'
                        }
                    }
                );

                if (response.data.success) {
                    setTokenValid(true);
                } else {
                    setError(response.data.message || 'Invalid or expired reset token.');
                    setTokenValid(false);
                }
            } catch (error) {
                console.error('Token verification error:', error);
                
                if (error.response) {
                    const { status, data } = error.response;
                    if (status === 404 || status === 400) {
                        setError(data.message || 'Invalid or expired reset token. Please request a new password reset link.');
                    } else {
                        setError(data.message || 'Failed to verify reset token. Please try again.');
                    }
                } else {
                    setError('Network error. Please check your connection and try again.');
                }
                setTokenValid(false);
            } finally {
                setIsVerifying(false);
            }
        };

        verifyToken();
    }, [token, email, decodedEmail]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setFieldErrors({});
        setIsSubmitting(true);

        // Validation
        const newErrors = {};
        
        if (!password.trim()) {
            newErrors.password = 'Password is required';
        } else if (password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        if (!confirmPassword.trim()) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (password !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        if (Object.keys(newErrors).length > 0) {
            setFieldErrors(newErrors);
            setIsSubmitting(false);
            return;
        }

        try {
            const response = await axios.post(
                getApiUrl('api/public/customer/reset-password-with-token'),
                {
                    token: token,
                    email: decodedEmail || email,
                    password: password.trim(),
                    password_confirmation: confirmPassword.trim()
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setSuccess(response.data.message || 'Password has been reset successfully. You can now login with your new password.');
                
                // Redirect to login after 3 seconds
                setTimeout(() => {
                    navigate('/customer/login');
                }, 3000);
            } else {
                setError(response.data.message || 'Failed to reset password. Please try again.');
            }
        } catch (error) {
            console.error('Reset password error:', error);
            
            if (error.response) {
                const { status, data } = error.response;
                
                if (status === 404 || status === 400) {
                    setError(data.message || 'Invalid or expired reset token. Please request a new password reset link.');
                } else if (status === 422) {
                    // Validation errors
                    if (data.errors) {
                        const apiErrors = {};
                        Object.keys(data.errors).forEach(key => {
                            if (key === 'password') {
                                apiErrors.password = Array.isArray(data.errors.password) 
                                    ? data.errors.password[0] 
                                    : data.errors.password;
                            } else if (key === 'password_confirmation') {
                                apiErrors.confirmPassword = Array.isArray(data.errors.password_confirmation) 
                                    ? data.errors.password_confirmation[0] 
                                    : data.errors.password_confirmation;
                            }
                        });
                        setFieldErrors(apiErrors);
                    }
                    setError(data.message || 'Please check the form and try again.');
                } else {
                    setError(data.message || 'Failed to reset password. Please try again.');
                }
            } else if (error.request) {
                setError('Network error. Please check your connection and try again.');
            } else {
                setError('An error occurred. Please try again later.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="customer-login-wrapper d-flex" style={{ 
            overflow: 'hidden',
            height: '100vh',
            maxHeight: '100vh',
            position: 'relative'
        }}>
            {/* Left Section - Background Image with Overlay */}
            <div className="d-none d-lg-flex flex-column justify-content-end" style={{
                width: '60%',
                backgroundImage: `url(${customerPortalImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                padding: '60px',
                paddingBottom: '30px',
                height: '100vh',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Full Black Overlay for Left Side */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    zIndex: 0
                }}></div>

                {/* Text Content */}
                <div style={{ 
                    maxWidth: '600px', 
                    position: 'relative', 
                    zIndex: 2,
                    marginBottom: '0'
                }}>
                    <h1 style={{ 
                        color: '#fff',
                        fontSize: '46px',
                        fontWeight: 'bold',
                        marginBottom: '20px',
                        lineHeight: '74px',
                        textAlign: 'left'
                    }}>
                        Reset Your <strong style={{ color: '#e7b06d' }}>Password</strong>
                    </h1>
                    <p style={{ 
                        color: '#fff',
                        fontSize: '20px',
                        fontWeight: '400',
                        marginBottom: '15px',
                        lineHeight: '34px',
                        textAlign: 'left'
                    }}>
                        <strong style={{ color: '#e7b06d' }}>Secure Password Reset</strong>
                    </p>
                    <p style={{ 
                        color: '#fff',
                        fontSize: '20px',
                        fontWeight: '400',
                        lineHeight: '34px',
                        textAlign: 'left'
                    }}>
                        Create a new password • Keep it secure • Login with confidence
                    </p>
                </div>
            </div>

            {/* Right Section - Green Background with Logo and Reset Password Form */}
            <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center" style={{
                backgroundColor: '#677a58',
                padding: '20px',
                position: 'relative',
                overflow: 'hidden',
                height: '100vh',
                maxHeight: '100vh'
            }}>
                {/* Decorative circles */}
                <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(215, 228, 213, 0.1)',
                    zIndex: 0
                }}></div>
                <div style={{
                    position: 'absolute',
                    bottom: '50px',
                    right: '30px',
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(231, 176, 109, 0.15)',
                    zIndex: 0
                }}></div>

                <div style={{ 
                    width: '100%',
                    maxWidth: '450px',
                    position: 'relative',
                    zIndex: 1,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                }}>
                    {/* Logo */}
                    <div className="text-center" style={{ marginBottom: '15px', flexShrink: 0 }}>
                        <img 
                            src={logo} 
                            alt="SDS Logo" 
                            style={{ 
                                height: '50px', 
                                marginBottom: '10px',
                                maxWidth: '180px'
                            }} 
                        />
                    </div>

                    <div style={{ padding: '15px', display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: '1', minHeight: 0 }}>
                        <h2 className="text-white mb-2" style={{ 
                            fontWeight: 'bold',
                            fontSize: '24px',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            marginBottom: '15px'
                        }}>
                            RESET PASSWORD
                        </h2>

                        {/* Loading State - Verifying Token */}
                        {isVerifying && (
                            <div className="text-center py-4">
                                <div className="spinner-border text-white mb-3" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="text-white">Verifying reset link...</p>
                            </div>
                        )}

                        {/* Error State - Invalid Token */}
                        {!isVerifying && !tokenValid && error && (
                            <div className="mb-3" style={{ 
                                borderRadius: '8px',
                                border: '2px solid #dc3545',
                                backgroundColor: '#dc3545',
                                color: '#fff',
                                padding: '15px',
                                textAlign: 'center'
                            }}>
                                <div style={{ 
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    marginBottom: '8px',
                                    color: '#fff'
                                }}>
                                    <i className="fa fa-exclamation-triangle me-2"></i>
                                    Invalid Reset Token
                                </div>
                                <p style={{ 
                                    marginBottom: '12px',
                                    color: '#fff',
                                    fontSize: '14px'
                                }}>
                                    {error}
                                </p>
                                <Button
                                    onClick={() => navigate('/customer/login')}
                                    backgroundColor="#ffffff"
                                    color="#dc3545"
                                    hoverBackgroundColor="#f8f9fa"
                                    borderRadius="6px"
                                    padding="8px 16px"
                                    fontSize="13px"
                                    fontWeight="600"
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#fff';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <i className="fa fa-arrow-left me-2"></i>
                                    Back to Login
                                </Button>
                            </div>
                        )}

                        {/* Success Message */}
                        {success && (
                            <Alert variant="success" className="mb-3" style={{ 
                                borderRadius: '8px',
                                border: 'none',
                                backgroundColor: '#d4edda',
                                color: '#155724',
                                padding: '12px',
                                fontSize: '14px'
                            }}>
                                {success}
                                <div className="mt-2" style={{ fontSize: '12px' }}>
                                    Redirecting to login page...
                                </div>
                            </Alert>
                        )}

                        {/* Reset Password Form */}
                        {!isVerifying && tokenValid && !success && (
                            <>
                                <div className="mb-3" style={{ color: '#d7e4d5', fontSize: '13px', lineHeight: '1.5' }}>
                                    <p className="mb-1">
                                        Enter your new password below. Make sure it's at least 6 characters long.
                                    </p>
                                    {decodedEmail && (
                                        <p className="mb-0" style={{ fontSize: '12px', opacity: 0.8 }}>
                                            Resetting password for: <strong>{decodedEmail}</strong>
                                        </p>
                                    )}
                                </div>

                                {error && (
                                    <Alert variant="danger" className="mb-3" style={{ 
                                        borderRadius: '10px',
                                        border: 'none',
                                        backgroundColor: '#f8d7da',
                                        color: '#721c24'
                                    }}>
                                        {error}
                                    </Alert>
                                )}

                                <Form onSubmit={handleSubmit} style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="text-white mb-1" style={{ fontSize: '13px', fontWeight: '500' }}>
                                            New Password
                                        </Form.Label>
                                        <div style={{ position: 'relative' }}>
                                            <Form.Control
                                                type={showPassword ? 'text' : 'password'}
                                                placeholder="Enter new password"
                                                value={password}
                                                onChange={(e) => {
                                                    setPassword(e.target.value);
                                                    if (fieldErrors.password) {
                                                        setFieldErrors(prev => {
                                                            const newErrors = { ...prev };
                                                            delete newErrors.password;
                                                            return newErrors;
                                                        });
                                                    }
                                                }}
                                                required
                                                disabled={isSubmitting}
                                                isInvalid={!!fieldErrors.password}
                                                style={{ 
                                                    borderRadius: '8px', 
                                                    border: fieldErrors.password ? '2px solid #dc3545' : 'none', 
                                                    padding: '10px 14px',
                                                    paddingRight: '45px',
                                                    fontSize: '14px',
                                                    backgroundColor: '#d7e4d5',
                                                    color: '#677a58',
                                                    transition: 'all 0.3s ease',
                                                    height: '42px'
                                                }}
                                                onFocus={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#fff';
                                                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(215, 228, 213, 0.5)';
                                                }}
                                                onBlur={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#d7e4d5';
                                                    e.currentTarget.style.boxShadow = 'none';
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                style={{
                                                    position: 'absolute',
                                                    right: '15px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    border: 'none',
                                                    background: 'none',
                                                    color: '#677a58',
                                                    cursor: 'pointer',
                                                    fontSize: '18px',
                                                    padding: '5px'
                                                }}
                                            >
                                                <span className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></span>
                                            </button>
                                        </div>
                                        {fieldErrors.password && (
                                            <Form.Control.Feedback type="invalid" style={{ display: 'block', color: '#f8d7da', fontSize: '13px', marginTop: '5px' }}>
                                                {fieldErrors.password}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="text-white mb-1" style={{ fontSize: '13px', fontWeight: '500' }}>
                                            Confirm New Password
                                        </Form.Label>
                                        <div style={{ position: 'relative' }}>
                                            <Form.Control
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                placeholder="Confirm new password"
                                                value={confirmPassword}
                                                onChange={(e) => {
                                                    setConfirmPassword(e.target.value);
                                                    if (fieldErrors.confirmPassword) {
                                                        setFieldErrors(prev => {
                                                            const newErrors = { ...prev };
                                                            delete newErrors.confirmPassword;
                                                            return newErrors;
                                                        });
                                                    }
                                                }}
                                                required
                                                disabled={isSubmitting}
                                                isInvalid={!!fieldErrors.confirmPassword}
                                                style={{ 
                                                    borderRadius: '8px', 
                                                    border: fieldErrors.confirmPassword ? '2px solid #dc3545' : 'none', 
                                                    padding: '10px 14px',
                                                    paddingRight: '45px',
                                                    fontSize: '14px',
                                                    backgroundColor: '#d7e4d5',
                                                    color: '#677a58',
                                                    transition: 'all 0.3s ease',
                                                    height: '42px'
                                                }}
                                                onFocus={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#fff';
                                                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(215, 228, 213, 0.5)';
                                                }}
                                                onBlur={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#d7e4d5';
                                                    e.currentTarget.style.boxShadow = 'none';
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                style={{
                                                    position: 'absolute',
                                                    right: '15px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    border: 'none',
                                                    background: 'none',
                                                    color: '#677a58',
                                                    cursor: 'pointer',
                                                    fontSize: '18px',
                                                    padding: '5px'
                                                }}
                                            >
                                                <span className={`fa ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></span>
                                            </button>
                                        </div>
                                        {fieldErrors.confirmPassword && (
                                            <Form.Control.Feedback type="invalid" style={{ display: 'block', color: '#f8d7da', fontSize: '13px', marginTop: '5px' }}>
                                                {fieldErrors.confirmPassword}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>

                                    <Button
                                        type="submit"
                                        className="w-100"
                                        disabled={isSubmitting}
                                        backgroundColor="#677a58"
                                        color="#ffffff"
                                        hoverBackgroundColor="#5a6a4a"
                                        height="40px"
                                        fontSize="14px"
                                        fontWeight="600"
                                        borderRadius="8px"
                                        textTransform="uppercase"
                                        letterSpacing="1px"
                                        marginBottom="10px"
                                    >
                                        {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
                                    </Button>

                                    <Button
                                        type="button"
                                        variant="text"
                                        className="w-100 text-decoration-none"
                                        backgroundColor="transparent"
                                        color="#d7e4d5"
                                        hoverBackgroundColor="transparent"
                                        onClick={() => navigate('/customer/login')}
                                        style={{ 
                                            color: '#d7e4d5',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            padding: '8px'
                                        }}
                                    >
                                        <i className="fa fa-arrow-left me-2"></i>
                                        Back to Login
                                    </Button>
                                </Form>
                            </>
                        )}
                    </div>
                    
                    {/* Footer */}
                    <div className="text-left" style={{ color: '#fff', fontSize: '11px', marginTop: '10px', flexShrink: 0 }}>
                        <p className="mb-0">
                            © 2025 SDS-Customer-Portal. All rights reserved. Designed and Developed by{' '}
                            <a 
                                href="https://veeyaainnovatives.com/" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#e7b06d',
                                    textDecoration: 'none',
                                    fontWeight: '500'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                            >
                                Veeyaa Innovatives
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerResetPassword;

